import { useEffect, useRef, useState } from 'react';
import { X, ScanLine, Camera } from 'lucide-react';

export interface BarcodeScannerModalProps {
  isOpen: boolean;
  onScan: (value: string, format: string) => void;
  onClose: () => void;
  prompt?: string;
}

type ScannerState = 'idle' | 'starting' | 'scanning' | 'scanned' | 'error';

/**
 * Shared modal wrapping html5-qrcode for barcode / QR scanning.
 * Dynamically imports the library to avoid bundle bloat.
 * Prefers the back camera, falls back to any available camera.
 */
export function BarcodeScannerModal({
  isOpen,
  onScan,
  onClose,
  prompt = 'Point the camera at a barcode or QR code',
}: BarcodeScannerModalProps) {
  const scannerRef = useRef<unknown>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState<ScannerState>('idle');
  const [scannedValue, setScannedValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Cleanup helper — stops scanning and releases the camera
  async function stopScanner() {
    if (!scannerRef.current) return;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const html5qr = scannerRef.current as any;
      if (html5qr.isScanning) {
        await html5qr.stop();
      }
      await html5qr.clear();
    } catch {
      // Swallow errors on cleanup — camera may already be released
    }
    scannerRef.current = null;
  }

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setState('starting');
    setScannedValue('');
    setErrorMessage('');

    async function startScanner() {
      // Dynamic import to avoid adding to the main bundle
      const { Html5Qrcode } = await import('html5-qrcode');

      if (cancelled || !containerRef.current) return;

      const containerId = 'barcode-scanner-container';
      containerRef.current.id = containerId;

      const html5qr = new Html5Qrcode(containerId);
      scannerRef.current = html5qr;

      const qrboxSize = Math.min(
        containerRef.current.offsetWidth * 0.7,
        280
      );

      try {
        await html5qr.start(
          { facingMode: { ideal: 'environment' } }, // back camera preferred
          {
            fps: 10,
            qrbox: { width: qrboxSize, height: Math.round(qrboxSize * 0.6) },
            aspectRatio: 1.333,
          },
          (decodedText: string, result: { result?: { format?: { formatName?: string } } }) => {
            if (cancelled) return;
            const format = result?.result?.format?.formatName ?? 'unknown';
            setScannedValue(decodedText);
            setState('scanned');
            // Brief flash of the scanned value, then call onScan
            setTimeout(() => {
              if (!cancelled) {
                stopScanner().finally(() => {
                  if (!cancelled) onScan(decodedText, format);
                });
              }
            }, 600);
          },
          () => {
            // Not-found callback — ignore, fires constantly while scanning
          }
        );
        if (!cancelled) setState('scanning');
      } catch (err) {
        if (cancelled) return;
        const message = (err as Error)?.message ?? String(err);
        if (
          message.toLowerCase().includes('permission') ||
          message.toLowerCase().includes('notallowed')
        ) {
          setErrorMessage(
            'Camera access was denied. Please allow camera permission in your browser settings and try again.'
          );
        } else {
          setErrorMessage(`Unable to start scanner: ${message}`);
        }
        setState('error');
      }
    }

    startScanner();

    return () => {
      cancelled = true;
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  function handleClose() {
    stopScanner().finally(onClose);
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal card — full-screen on mobile, constrained on desktop */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Scan Barcode"
        className="relative z-10 w-full sm:max-w-md sm:mx-4 bg-white sm:rounded-xl overflow-hidden shadow-2xl flex flex-col max-h-screen sm:max-h-[90vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-300 flex-shrink-0">
          <div className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-navy-600" />
            <h2 className="text-base font-semibold text-slate-900">Scan Barcode</h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label="Close scanner"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Camera viewport */}
        <div className="relative bg-black flex-shrink-0" style={{ aspectRatio: '4/3' }}>
          <div
            ref={containerRef}
            className="w-full h-full"
          />

          {/* Starting overlay */}
          {state === 'starting' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black gap-3">
              <Camera className="w-10 h-10 text-slate-400 animate-pulse" />
              <p className="text-sm text-slate-400">Starting camera…</p>
            </div>
          )}

          {/* Scanned flash */}
          {state === 'scanned' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 gap-3">
              <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center">
                <ScanLine className="w-8 h-8 text-white" />
              </div>
              <p className="text-sm font-medium text-white truncate max-w-xs px-4 text-center">
                {scannedValue}
              </p>
            </div>
          )}

          {/* Error overlay */}
          {state === 'error' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black gap-4 p-6">
              <Camera className="w-10 h-10 text-red-400" />
              <p className="text-sm text-slate-300 text-center">{errorMessage}</p>
              <button
                type="button"
                onClick={handleClose}
                className="min-h-[44px] px-4 py-2 text-sm font-medium bg-white text-slate-900 rounded-md hover:bg-slate-100 transition-colors"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>

        {/* Prompt */}
        {state === 'scanning' && (
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex-shrink-0">
            <p className="text-sm text-slate-600 text-center">{prompt}</p>
          </div>
        )}
      </div>
    </div>
  );
}
